import { Ban, BatteryLow, Clock, Pencil } from 'lucide-react';
import { Eyebrow } from '@/components/ui';
import type { SkipReason } from '@/types';

const SKIP_REASONS: { value: SkipReason; label: string; Icon: typeof Clock }[] = [
  { value: 'low_energy', label: 'Low energy', Icon: BatteryLow },
  { value: 'out_of_time', label: 'Out of time', Icon: Clock },
  { value: 'not_relevant', label: 'Not relevant', Icon: Ban },
  { value: 'other', label: 'Other', Icon: Pencil },
];

export interface SkipReasonGridProps {
  onPick: (reason: SkipReason) => void;
}

/**
 * Shared "Why skip?" chooser used by both the inbox SkipReasonPopover and the
 * editor slide-over, so the same concept renders identically everywhere:
 * mono eyebrow header, lucide glyphs (no decorative emoji), and a neutral
 * "Just skip" escape hatch.
 */
export function SkipReasonGrid({ onPick }: SkipReasonGridProps) {
  return (
    <div>
      <Eyebrow className="mb-1.5 px-1" aria-hidden>
        Why skip?
      </Eyebrow>
      <div className="grid grid-cols-2 gap-1" role="group" aria-label="Why skip?">
        {SKIP_REASONS.map((r) => (
          <button
            key={r.value}
            type="button"
            onClick={() => onPick(r.value)}
            className="pressable flex flex-col items-center gap-1 rounded-md border border-line-2 bg-bg px-2 py-2 text-2xs text-t1 can-hover:hover:bg-card pointer-coarse:min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
          >
            <r.Icon className="h-4 w-4 text-t3" aria-hidden />
            <span>{r.label}</span>
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onPick('other')}
        className="mt-2 w-full rounded-md py-1 text-center text-2xs text-t3 can-hover:hover:text-t1 pointer-coarse:min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
      >
        Just skip
      </button>
    </div>
  );
}
